import axios from "@/api/api";
import {
  type EvolutionPath,
  evolutionPathFromParam,
} from "@repo/shared/evolutions";
import type { LoaderFunctionArgs } from "react-router";

export type ImageType = "with-evolution-chain" | "detailed-player-card";
export type PlayerType =
  | "player"
  | "evolved"
  | "evolution"
  | "evo-builder"
  | "lab";
export type AspectPlayerImageType = "1:1" | "16:9" | "2:3";

export type PlayerImageResponse = {
  url: string;
  aspectRatio: AspectPlayerImageType;
  playerImageType: ImageType;
};

export type PlayerImagesLoaderResponse = {
  resourceId: string;
  name: string | undefined;
  version: string | undefined;
  evolutionChain: string | null;
  evoPathId: string | null;
  isEvoBuilder: boolean;
  playerImages: Promise<PlayerImageResponse[]>;
  playerImageType: ImageType;
};

export const playerImagesLoader = ({
  request,
  params,
}: LoaderFunctionArgs): PlayerImagesLoaderResponse => {
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const evolutionChain = searchParams.get("evolution-path");
  const evolutionPath = evolutionPathFromParam(evolutionChain);
  const evoPathId = searchParams.get("evo-path-id");
  const isEvoBuilder =
    searchParams.get("is-evo-builder") === "true" && !!evolutionChain;
  const isPlayStylesLab = searchParams.get("is-playstyles-lab") === "true";
  const labPlayStyles = searchParams.get("lab-playstyles");
  const labPlayStylesPlus = searchParams.get("lab-playstyles-plus");
  const playerImageType = searchParams.get("player-image-type") as ImageType;

  const baseUrlSlug: PlayerType = isPlayStylesLab
    ? "lab"
    : isEvoBuilder
      ? "evo-builder"
      : evolutionChain
        ? "evolution"
        : evoPathId
          ? "evolved"
          : "player";

  const playerImagesPromise = Promise.allSettled([
    getPlayerImages({
      urlSlug: baseUrlSlug,
      playerImageType: "detailed-player-card",
      resourceId: params.resourceId!,
      aspect: "1:1",
      evolutionPath,
      evoPathId,
      isPlayStylesLab,
      labPlayStyles,
      labPlayStylesPlus,
    }),
    getPlayerImages({
      urlSlug: baseUrlSlug,
      playerImageType: "detailed-player-card",
      resourceId: params.resourceId!,
      aspect: "16:9",
      evoPathId,
      evolutionPath,
      isPlayStylesLab,
      labPlayStyles,
      labPlayStylesPlus,
    }),
    getPlayerImages({
      urlSlug: baseUrlSlug,
      playerImageType: "detailed-player-card",
      resourceId: params.resourceId!,
      aspect: "2:3",
      evoPathId,
      evolutionPath,
      isPlayStylesLab,
      labPlayStyles,
      labPlayStylesPlus,
    }),
    ...(evolutionChain
      ? [
          getPlayerImages({
            urlSlug: isEvoBuilder ? "evo-builder" : "evolution",
            playerImageType: "with-evolution-chain",
            resourceId: params.resourceId!,
            aspect: "16:9",
            evolutionPath,
            evoPathId,
          }),
          getPlayerImages({
            urlSlug: isEvoBuilder ? "evo-builder" : "evolution",
            playerImageType: "with-evolution-chain",
            resourceId: params.resourceId!,
            aspect: "1:1",
            evolutionPath,
            evoPathId,
          }),
        ]
      : []),
  ]).then((results) =>
    results
      .filter(
        (r): r is PromiseFulfilledResult<PlayerImageResponse> =>
          r.status === "fulfilled",
      )
      .map((r) => ({
        url: r.value.url,
        aspectRatio: r.value.aspectRatio,
        playerImageType: r.value.playerImageType,
      })),
  );

  return {
    resourceId: params.resourceId,
    name: params.name,
    version: params.version,
    evolutionChain: evolutionChain,
    evoPathId: evoPathId,
    isEvoBuilder: isEvoBuilder,
    playerImages: playerImagesPromise,
    playerImageType: playerImageType,
  };
};

const getPlayerImages = async ({
  urlSlug,
  playerImageType,
  resourceId,
  aspect,
  evolutionPath,
  evoPathId,
  isPlayStylesLab,
  labPlayStyles,
  labPlayStylesPlus,
}: {
  urlSlug: PlayerType;
  playerImageType: ImageType;
  resourceId: string;
  aspect: AspectPlayerImageType;
  evolutionPath?: EvolutionPath;
  evoPathId?: string;
  isPlayStylesLab?: boolean;
  labPlayStyles?: string;
  labPlayStylesPlus?: string;
}) => {
  const res = await axios.post<PlayerImageResponse>(
    `/player-images/${urlSlug}/${resourceId}`,
    {
      aspectRatio: aspect,
      ...(evolutionPath
        ? {
            evoPath: evolutionPath.evolutionPath,
            pathwayPath: evolutionPath.pathwayPath,
          }
        : {}),
      ...(evoPathId ? { evolutionHash: evoPathId } : {}),
      ...(isPlayStylesLab
        ? {
            labPlayStyles: labPlayStyles?.split(",")?.map(Number) ?? [],
            labPlayStylesPlus: labPlayStylesPlus?.split(",")?.map(Number) ?? [],
          }
        : {}),
      imageType: playerImageType,
    },
  );
  return res.data;
};
